import {
	useAddressAutocompleteLazyQuery,
	useGenerateVatFromRegistrationLazyQuery,
	useGetAllLocalizationCountriesQuery,
	useGetLocalizationStatesQuery,
	useGetPlaceDetailsLazyQuery,
	useValidateCvrLazyQuery,
} from '@codezo/graphql';
import {
	Autocomplete,
	Box,
	Divider,
	Group,
	Loader,
	LoadingOverlay,
	Select,
	SimpleGrid,
	Stack,
	TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDebouncedValue } from '@mantine/hooks';
import { IconAlertCircle, IconCheck, IconMapPin } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';

interface CountryOrganizationStepProps {
	data: Record<string, unknown>;
	onDataChange: (_data: Record<string, unknown>) => void;
	onValidSubmit: (_data: Record<string, unknown>) => void;
	onPrevious: () => void;
	isFirstStep: boolean;
	isLastStep: boolean;
	triggerValidation: number;
}

export function CountryOrganizationStep({
	data,
	onDataChange,
	onValidSubmit,
	triggerValidation,
}: CountryOrganizationStepProps) {
	const lastTriggerRef = useRef(0);
	const [cvrValidating, setCvrValidating] = useState(false);
	const [cvrValidated, setCvrValidated] = useState<boolean | null>(null);
	const [cvrError, setCvrError] = useState<string | null>(null);
	const [lastValidatedCvr, setLastValidatedCvr] = useState<string | null>(null);
	const [addressSearch, setAddressSearch] = useState('');
	const [suggestions, setSuggestions] = useState<Array<{ value: string; placeId: string }>>([]);
	const [isSearching, setIsSearching] = useState(false);
	const [sessionToken] = useState(() => crypto.randomUUID());

	// Fetch all countries
	const { data: countriesData, loading: countriesLoading } =
		useGetAllLocalizationCountriesQuery();

	// Queries
	const [validateCvr] = useValidateCvrLazyQuery();
	const [searchAddress] = useAddressAutocompleteLazyQuery();
	const [getPlaceDetails] = useGetPlaceDetailsLazyQuery();
	const [generateVatFromRegistration] = useGenerateVatFromRegistrationLazyQuery();

	const countryOptions = useMemo(() => {
		if (!countriesData?.getAllLocalizationCountries) return [];
		return countriesData.getAllLocalizationCountries.map((country) => ({
			value: country.code,
			label: country.name,
		}));
	}, [countriesData]);

	// Get selected country details
	const selectedCountry = useMemo(() => {
		if (!countriesData?.getAllLocalizationCountries || !data.countryCode) return null;
		return countriesData.getAllLocalizationCountries.find(
			(country) => country.code === data.countryCode
		);
	}, [countriesData, data.countryCode]);

	// Initialize business registrations based on existing data
	const initialBusinessRegistrations = useMemo(() => {
		const registrations: Record<string, string> = {};
		if (Array.isArray(data.businessRegistrations)) {
			(data.businessRegistrations as Array<{ type: string; number: string }>).forEach(
				(reg) => {
					registrations[reg.type] = reg.number;
				}
			);
		}
		return registrations;
	}, [data.businessRegistrations]);

	// Parse existing address data
	const initialAddress = useMemo(() => {
		if (data.address && typeof data.address === 'object') {
			return data.address as Record<string, unknown>;
		}
		return {
			addressLine1: '',
			addressLine2: '',
			city: '',
			stateCode: '',
			postalCode: '',
			countryCode: '',
			googlePlaceId: '',
			latitude: null,
			longitude: null,
		};
	}, [data.address]);

	const form = useForm({
		initialValues: {
			countryCode: (data.countryCode as string) || '',
			organizationName: (data.organizationName as string) || '',
			websiteUrl: (data.websiteUrl as string) || '',
			industry: (data.industry as string) || '',
			businessRegistrations: initialBusinessRegistrations,
			address: initialAddress,
		},
		validate: {
			countryCode: (value) =>
				value ? null : 'Please select an address to auto-detect country or select manually',
			organizationName: (value) => (value.trim() ? null : 'Name is required'),
			websiteUrl: (value) => {
				if (!value) return null; // Optional
				try {
					const _url = new URL(value);
					void _url; // URL validation only
					return null;
				} catch {
					return 'Please enter a valid URL';
				}
			},
			businessRegistrations: {
				// Validate each registration individually
				...Object.fromEntries(
					selectedCountry?.registrations?.map((reg) => [
						reg.code,
						(value: string) => {
							if (reg.isRequired && (!value || value.trim() === '')) {
								return `${reg.label} is required`;
							}
							if (reg.code === 'CVR' && value) {
								// Special validation for CVR
								if (value.length !== 8) {
									return 'CVR must be exactly 8 digits';
								}
								if (!/^\d{8}$/.test(value)) {
									return 'CVR must contain only digits';
								}
							}
							return null;
						},
					]) || []
				),
			},
			address: {
				addressLine1: (value: string) => (value?.trim() ? null : 'Address is required'),
				city: (value: string) => (value?.trim() ? null : 'City is required'),
				// Postal code is optional - some countries don't use them or Google Places doesn&apos;t always provide them
				postalCode: () => null,
			},
		},
	});

	// Watch for CVR or other registration validations
	const cvrRegistration = selectedCountry?.registrations?.find((reg) => reg.code === 'CVR');
	const [cvrNumber] = useDebouncedValue(
		cvrRegistration ? form.values.businessRegistrations?.CVR || '' : '',
		500
	);
	const [debouncedSearch] = useDebouncedValue(addressSearch, 300);

	// Get states for selected country
	const { data: statesData } = useGetLocalizationStatesQuery({
		variables: { countryCode: form.values.countryCode },
		skip: !form.values.countryCode || !selectedCountry?.hasStates,
	});

	const stateOptions = useMemo(() => {
		if (!statesData?.getLocalizationStates) return [];
		return statesData.getLocalizationStates.map((state) => ({
			value: state.code,
			label: state.name,
		}));
	}, [statesData]);

	// Search for addresses when input changes (no country restriction initially)
	useEffect(() => {
		if (debouncedSearch && debouncedSearch.length > 2) {
			setIsSearching(true);
			searchAddress({
				variables: {
					input: debouncedSearch,
					// Don&apos;t restrict by country initially - let user search globally
					countryCode: null,
					sessionToken,
				},
			})
				.then(({ data: autocompleteData }) => {
					if (autocompleteData?.addressAutocomplete) {
						setSuggestions(
							autocompleteData.addressAutocomplete.map((result) => ({
								value: result.description,
								placeId: result.placeId,
							}))
						);
					}
				})
				.finally(() => {
					setIsSearching(false);
				});
		} else {
			setSuggestions([]);
		}
	}, [debouncedSearch, sessionToken, searchAddress]);

	// Handle address selection from autocomplete
	const handleAddressSelect = async (item: { value: string; placeId: string }) => {
		try {
			const { data: placeData } = await getPlaceDetails({
				variables: {
					placeId: item.placeId,
					sessionToken,
				},
			});

			if (placeData?.getPlaceDetails) {
				const details = placeData.getPlaceDetails;

				// Build address line 1
				const addressLine1 = [details.streetNumber, details.streetName]
					.filter(Boolean)
					.join(' ');

				// Update form with place details including country
				form.setFieldValue('address', {
					addressLine1: addressLine1 || details.formattedAddress || '',
					addressLine2: '',
					city: details.city || '',
					stateCode: details.stateCode || '',
					postalCode: details.postalCode || '',
					countryCode: details.countryCode || '',
					googlePlaceId: details.placeId,
					latitude: details.latitude,
					longitude: details.longitude,
				});

				// Also update the main country field from the place details
				if (details.countryCode) {
					// Check if the country is changing
					const isCountryChanging =
						form.values.countryCode && form.values.countryCode !== details.countryCode;

					form.setFieldValue('countryCode', details.countryCode);

					// Clear business registrations when country changes to prevent carrying over invalid data
					if (isCountryChanging) {
						form.setFieldValue('businessRegistrations', {});
					}
				}

				// If timezone is detected, update parent data
				if (details.timezone) {
					onDataChange({
						...form.values,
						address: form.values.address,
						countryCode: details.countryCode || '',
						timezone: details.timezone,
					});
				}
			}
		} catch {
			// Error handled by mutation onError
		}
	};

	// Validate CVR when it changes (for any country with CVR registration)
	useEffect(() => {
		// Skip if we already validated this CVR
		if (cvrNumber === lastValidatedCvr) {
			return;
		}

		const hasCvrRegistration = selectedCountry?.registrations?.some(
			(reg) => reg.code === 'CVR'
		);
		if (hasCvrRegistration && cvrNumber) {
			// Check for non-digit characters
			if (!/^\d*$/.test(cvrNumber)) {
				setCvrError('CVR must contain only digits');
				setCvrValidated(false);
				setLastValidatedCvr(cvrNumber);
			} else if (cvrNumber.length > 8) {
				// Too many digits
				setCvrError('CVR must be exactly 8 digits');
				setCvrValidated(false);
				setLastValidatedCvr(cvrNumber);
			} else if (cvrNumber.length === 8) {
				// Exactly 8 digits - validate with API
				setCvrValidating(true);
				setCvrError(null);
				setLastValidatedCvr(cvrNumber);

				validateCvr({
					variables: { cvrNumber },
				})
					.then(({ data: validationData }) => {
						if (validationData?.validateCVR) {
							const { companyInfo } = validationData.validateCVR;
							if (validationData.validateCVR.valid && companyInfo) {
								// Auto-fill company information
								// Check if country is changing before updating
								const currentCountry = form.values.countryCode;
								const isCountryChanging = currentCountry && currentCountry !== 'DK';

								form.setValues((current) => ({
									...current,
									organizationName: companyInfo.name,
									address: {
										...current.address,
										addressLine1:
											companyInfo.address?.street ||
											(current.address as Record<string, unknown>)
												.addressLine1,
										city:
											companyInfo.address?.city ||
											(current.address as Record<string, unknown>).city,
										postalCode:
											companyInfo.address?.zipcode ||
											(current.address as Record<string, unknown>).postalCode,
										countryCode: 'DK',
									},
									businessRegistrations: isCountryChanging
										? {
												// Clear all registrations if country changed
												CVR: cvrNumber,
												VAT: companyInfo.vat ? `DK${companyInfo.vat}` : '',
											}
										: {
												// Keep existing registrations if same country
												...current.businessRegistrations,
												CVR: cvrNumber,
												VAT: companyInfo.vat ? `DK${companyInfo.vat}` : '',
											},
								}));

								// Also update the country field
								form.setFieldValue('countryCode', 'DK');

								setCvrValidated(true);
								setCvrError(null);
							} else {
								setCvrError(
									validationData.validateCVR.error || 'CVR number not found'
								);
								setCvrValidated(false);
							}
						} else {
							setCvrError('CVR number not found');
							setCvrValidated(false);
						}
					})
					.catch(() => {
						setCvrError('Failed to validate CVR number');
						setCvrValidated(false);
					})
					.finally(() => {
						setCvrValidating(false);
					});
			} else if (cvrNumber.length > 0 && cvrNumber.length < 8) {
				// Less than 8 digits - show error
				setCvrError('CVR must be 8 digits');
				setCvrValidated(false);
				setLastValidatedCvr(cvrNumber);
			} else {
				// Empty or being typed
				setCvrValidated(null);
				setCvrError(null);
				setLastValidatedCvr(null);
			}
		} else {
			// Reset validation state if CVR is cleared or not Denmark
			setCvrValidated(null);
			setCvrError(null);
			setLastValidatedCvr(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cvrNumber, selectedCountry, validateCvr, lastValidatedCvr]);

	// When country changes, update the form to show relevant business registrations
	useEffect(() => {
		if (selectedCountry) {
			// Auto-fill defaults from country
			const defaults: Record<string, unknown> = {
				currency: selectedCountry.currency,
				timezone: selectedCountry.defaultTimezone,
				measurementSystem: selectedCountry.defaultMeasurementSystem,
			};

			// Update parent with country defaults
			onDataChange({
				...form.values,
				...defaults,
				businessRegistrations: Object.entries(form.values.businessRegistrations)
					.filter(([_, value]) => value)
					.map(([type, number]) => ({ type, number })),
			});
		}
	}, [selectedCountry, form.values, onDataChange]);

	// Update parent component whenever form values change
	useEffect(() => {
		const businessRegistrationsArray = Object.entries(form.values.businessRegistrations)
			.filter(([_, value]) => value)
			.map(([type, number]) => ({ type, number }));

		onDataChange({
			...form.values,
			businessRegistrations: businessRegistrationsArray,
		});
	}, [form.values, onDataChange]);

	// Handle validation trigger from parent
	useEffect(() => {
		if (triggerValidation > 0 && triggerValidation !== lastTriggerRef.current) {
			lastTriggerRef.current = triggerValidation;

			const validationResult = form.validate();

			// Additional validation for all business registrations
			let hasAdditionalErrors = false;
			const businessRegistrationErrors: Record<string, string> = {};

			// Check all required business registrations
			if (selectedCountry?.registrations) {
				for (const registration of selectedCountry.registrations) {
					const value = form.values.businessRegistrations[registration.code];

					// Check if required field is missing
					if (registration.isRequired && (!value || value.trim() === '')) {
						businessRegistrationErrors[registration.code] =
							`${registration.label} is required`;
						hasAdditionalErrors = true;
					}

					// Special validation for CVR
					if (registration.code === 'CVR' && value) {
						if (cvrValidating) {
							// Still validating, don't proceed
							businessRegistrationErrors[registration.code] =
								'Please wait for CVR validation to complete';
							hasAdditionalErrors = true;
						} else if (cvrValidated === false) {
							// CVR validation failed
							businessRegistrationErrors[registration.code] =
								cvrError || 'CVR number not found in registry';
							hasAdditionalErrors = true;
						} else if (cvrValidated === null && value.length === 8) {
							// CVR looks valid but hasn't been validated yet
							businessRegistrationErrors[registration.code] =
								'Please wait for CVR validation to complete';
							hasAdditionalErrors = true;
						}
					}
				}
			}

			// Set business registration errors if any exist
			if (Object.keys(businessRegistrationErrors).length > 0) {
				Object.entries(businessRegistrationErrors).forEach(([field, error]) => {
					form.setFieldError(`businessRegistrations.${field}`, error);
				});
			}

			if (!validationResult.hasErrors && !hasAdditionalErrors) {
				const businessRegistrationsArray = Object.entries(form.values.businessRegistrations)
					.filter(([_, value]) => value)
					.map(([type, number]) => ({ type, number }));

				onValidSubmit({
					...form.values,
					businessRegistrations: businessRegistrationsArray,
				});
			}
		}
	}, [
		triggerValidation,
		cvrValidated,
		cvrValidating,
		cvrError,
		selectedCountry,
		form,
		onValidSubmit,
	]);

	const handleSubmit = (values: typeof form.values) => {
		// Validate all business registrations before submitting
		const businessRegistrationErrors: Record<string, string> = {};
		let hasErrors = false;

		if (selectedCountry?.registrations) {
			for (const registration of selectedCountry.registrations) {
				const value = values.businessRegistrations[registration.code];

				// Check if required field is missing
				if (registration.isRequired && (!value || value.trim() === '')) {
					businessRegistrationErrors[registration.code] =
						`${registration.label} is required`;
					hasErrors = true;
				}

				// Special validation for CVR
				if (registration.code === 'CVR' && value) {
					if (cvrValidating) {
						businessRegistrationErrors[registration.code] =
							'Please wait for CVR validation to complete';
						hasErrors = true;
					} else if (cvrValidated === false) {
						businessRegistrationErrors[registration.code] =
							cvrError || 'CVR number not found in registry';
						hasErrors = true;
					} else if (cvrValidated === null && value.length === 8) {
						businessRegistrationErrors[registration.code] =
							'Please wait for CVR validation to complete';
						hasErrors = true;
					}
				}
			}
		}

		// Set business registration errors if any exist
		if (hasErrors) {
			Object.entries(businessRegistrationErrors).forEach(([field, error]) => {
				form.setFieldError(`businessRegistrations.${field}`, error);
			});
			return;
		}

		const businessRegistrationsArray = Object.entries(values.businessRegistrations)
			.filter(([_, value]) => value)
			.map(([type, number]) => ({ type, number }));

		onValidSubmit({
			...values,
			businessRegistrations: businessRegistrationsArray,
		});
	};

	return (
		<Box pos="relative">
			<LoadingOverlay visible={countriesLoading} />
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack gap="lg">
					{/* Address Search First */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4, delay: 0.1 }}
					>
						<Stack gap="md">
							{/* Address search first - no country required */}
							<Autocomplete
								label="Search for your address"
								description="Start typing to search for your address and auto-fill location details"
								placeholder="Start typing your address..."
								data={suggestions}
								value={addressSearch}
								onChange={setAddressSearch}
								onOptionSubmit={(value) => {
									const suggestion = suggestions.find((s) => s.value === value);
									if (suggestion) {
										handleAddressSelect(suggestion);
										setAddressSearch('');
									}
								}}
								leftSection={<IconMapPin size={16} />}
								rightSection={isSearching ? <Loader size="xs" /> : null}
							/>

							{/* Address fields */}
							<SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
								<TextInput
									label="Address Line 1"
									placeholder="Street address"
									required
									{...form.getInputProps('address.addressLine1')}
									onChange={(event) => {
										form.setFieldValue(
											'address.addressLine1',
											event.currentTarget.value
										);
									}}
								/>

								<TextInput
									label="Address Line 2"
									placeholder="Apartment, suite, etc."
									{...form.getInputProps('address.addressLine2')}
									onChange={(event) => {
										form.setFieldValue(
											'address.addressLine2',
											event.currentTarget.value
										);
									}}
								/>

								<TextInput
									label="City"
									placeholder="City"
									required
									{...form.getInputProps('address.city')}
									onChange={(event) => {
										form.setFieldValue(
											'address.city',
											event.currentTarget.value
										);
									}}
								/>

								{selectedCountry?.hasStates && (
									<Select
										label={
											selectedCountry.addressFormat?.stateLabel ||
											'State/Province'
										}
										placeholder="Select state/province"
										data={stateOptions}
										searchable
										required={selectedCountry.addressFormat?.stateRequired}
										{...form.getInputProps('address.stateCode')}
										onChange={(value) => {
											form.setFieldValue('address.stateCode', value || '');
										}}
									/>
								)}

								<TextInput
									label={
										selectedCountry?.addressFormat?.postalCodeLabel ||
										'Postal Code'
									}
									placeholder="Postal code (optional)"
									{...form.getInputProps('address.postalCode')}
									onChange={(event) => {
										form.setFieldValue(
											'address.postalCode',
											event.currentTarget.value
										);
									}}
								/>
							</SimpleGrid>

							{/* Country field - auto-populated from address */}
							<Select
								label="Country"
								placeholder="Select your country"
								data={countryOptions}
								searchable
								required
								{...form.getInputProps('countryCode')}
								onChange={(value) => {
									form.setFieldValue('countryCode', value || '');
									form.setFieldValue('address.countryCode', value || '');
									// Reset business registrations when country changes
									form.setFieldValue('businessRegistrations', {});
								}}
							/>
						</Stack>
					</motion.div>

					{/* Divider */}
					<Divider />

					{/* Dynamic business registrations based on country */}
					{selectedCountry?.registrations && selectedCountry.registrations.length > 0 && (
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.4, delay: 0.2 }}
						>
							<SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
								{selectedCountry.registrations.map((registration) => (
									<TextInput
										key={registration.code}
										label={registration.label}
										placeholder={registration.placeholder || undefined}
										description={registration.helpText}
										required={registration.isRequired}
										value={
											form.values.businessRegistrations[registration.code] ||
											''
										}
										onChange={async (event) => {
											const { value } = event.currentTarget;
											form.setFieldValue(
												`businessRegistrations.${registration.code}`,
												value
											);

											// Auto-generate VAT if this registration has a VAT derivation pattern
											if (
												registration.vatDerivationPattern &&
												value &&
												form.values.countryCode
											) {
												try {
													const { data: vatData } =
														await generateVatFromRegistration({
															variables: {
																countryCode:
																	form.values.countryCode,
																registrationNumber: value,
																registrationType: registration.code,
															},
														});

													if (vatData?.generateVATFromRegistration) {
														// Find VAT field and update it
														const vatField =
															selectedCountry.registrations.find(
																(reg) => reg.code === 'VAT'
															);
														if (vatField) {
															form.setFieldValue(
																'businessRegistrations.VAT',
																vatData.generateVATFromRegistration
															);
														}
													}
												} catch {
													// Error handled by mutation onError
												}
											}
										}}
										onBlur={() => form.validateField('businessRegistrations')}
										rightSection={
											registration.code === 'CVR' ? (
												<Group gap={4}>
													{cvrValidating && <Loader size="xs" />}
													{cvrValidated === true && (
														<IconCheck size={16} color="green" />
													)}
													{cvrValidated === false && !cvrValidating && (
														<IconAlertCircle size={16} color="red" />
													)}
												</Group>
											) : null
										}
										error={
											registration.code === 'CVR'
												? cvrError ||
													(
														form.errors.businessRegistrations as
															| Record<string, string>
															| undefined
													)?.[registration.code] ||
													undefined
												: (
														form.errors.businessRegistrations as
															| Record<string, string>
															| undefined
													)?.[registration.code] || undefined
										}
									/>
								))}
							</SimpleGrid>
						</motion.div>
					)}

					{/* Organization Details */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{
							duration: 0.4,
							delay: form.values.countryCode === 'DK' ? 0.3 : 0.2,
						}}
					>
						<SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
							<TextInput
								label="Name"
								placeholder="Your organization name"
								required
								{...form.getInputProps('organizationName')}
								onChange={(event) => {
									form.setFieldValue(
										'organizationName',
										event.currentTarget.value
									);
								}}
							/>

							<TextInput
								label="Website"
								placeholder="https://example.com"
								{...form.getInputProps('websiteUrl')}
								onChange={(event) => {
									form.setFieldValue('websiteUrl', event.currentTarget.value);
								}}
							/>
						</SimpleGrid>
					</motion.div>
				</Stack>
			</form>
		</Box>
	);
}
